/*
 * nlp-quiz.js — engine for the Nono "treningskalkulator" quiz funnel (design v2).
 *
 * Ground truth is sections/nlp-quiz.liquid: it renders the full static markup for
 * every screen (intro · Q1..Q6 · computing · result) plus a
 * <script type="application/json" id="nlp-quiz-config"> that carries ALL copy and
 * every logic table. This file holds NO Norwegian literals — it reads them from
 * that config and drives the class/DOM contract the section renders and the CSS
 * styles.
 *
 * It is a VERBATIM behavioural port of the design's `class Component`
 * (source/Nono Treningskalkulator.dc.html):
 *   · state { step,name,lastname,phone,mirror,goals,body,doctor,callbackRequested }
 *   · start / goNext (step 7 → 1900ms → step 8) / back / pick (240ms auto-advance)
 *   · toggle (none-clears-others + others-clear-none) / onName/onLastname/onPhone
 *   · requestCallback / ackFor(step 3 & 6) / computeProfile (archetype cascade,
 *     match 88→97, reassurance dedupe max 4, focus FMAP, goalPhrase PMAP,
 *     reassureTitle) / dynamic Neste labels / dynamic contact title / can-gating
 *   · single-screen show via .is-active/[hidden]; scrollTo top on each step change;
 *     progress "Steg N av {totalSteps}"
 *
 * OUR WIRING (added on top of the design):
 *   · quiz_start beacon + fbq QuizStart on start()
 *   · ONE background /api/lead POST when advancing FROM the contact step (4 → 5),
 *     keepalive, never blocking the flow (advance optimistically; retry once on
 *     failure, then continue silently; on 200 → quiz_complete + fbq QuizComplete)
 *   · visit-attribution beacons (quiz_start / quiz_complete / quiz_callback)
 *   · sessionStorage persist/restore (key from config, nono_quiz_v2)
 *   · no autofocus; visualViewport keeps the contact "Neste" pill above the keyboard
 *
 * Booking is NOT implemented here: the close card's [data-booking-trigger] is
 * caught by nono-own-booking.js via document-level click delegation, so this file
 * leaves it entirely intact (and never client-fires a Meta 'Lead' — the server
 * owns CAPI 'Lead').
 *
 * Dependency-free IIFE, no build step. Guarded throughout; never throws to the user.
 */
(function () {
  'use strict';

  try {
    var root = document.querySelector('.quiz-app');
    if (!root) return;

    var cfgEl = document.getElementById('nlp-quiz-config');
    if (!cfgEl) return;

    var C;
    try {
      C = JSON.parse(cfgEl.textContent || cfgEl.innerText || '{}');
    } catch (e) {
      return; // malformed config — bail rather than render a broken quiz
    }
    if (!C || !C.settings || !Array.isArray(C.questions)) return;

    /* ---------------------------------------------------------------------- *
     * Small DOM helpers (closest/matches fallbacks for older webviews)
     * ---------------------------------------------------------------------- */
    function matches(el, sel) {
      if (!el || el.nodeType !== 1) return false;
      var fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
      return fn ? fn.call(el, sel) : false;
    }
    function closest(el, sel) {
      while (el && el.nodeType === 1) {
        if (matches(el, sel)) return el;
        el = el.parentElement || el.parentNode;
      }
      return null;
    }
    function qs(el, sel) { return el ? el.querySelector(sel) : null; }
    function setText(el, str) { if (el) el.textContent = (str == null ? '' : str); }
    function attr(name) { var v = root.getAttribute(name); return v == null ? '' : v; }
    function focusEl(el) {
      if (!el) return;
      try { el.focus({ preventScroll: true }); }
      catch (e) { try { el.focus(); } catch (e2) {} }
    }
    function scrollTop() {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); }
      catch (e) { try { window.scrollTo(0, 0); } catch (e2) {} }
    }

    /* ---------------------------------------------------------------------- *
     * Settings + config tables
     * ---------------------------------------------------------------------- */
    var S = C.settings;
    var BEACON = C.beacon || {};
    var LEAD = C.lead || {};
    var VALIDATE = C.validate || {};
    var NEXTLABELS = C.nextLabels || {};
    var ACK = C.ack || {};
    var PROFILE = C.profile || {};
    var RESULT = C.result || {};
    var CLOSE = C.close || {};
    var PROGRESS = C.progress || {};

    var apiBase = String(S.apiBase || attr('data-api-base') || '').replace(/\/+$/, '');
    var sourceId = (S.sourceId != null ? S.sourceId : attr('data-source-id'));
    var storageKey = S.storageKey || 'nono_quiz_v2';
    var AUTO_MS = Number(S.autoAdvanceMs != null ? S.autoAdvanceMs : 240);
    var COMPUTING_MS = Number(S.computingMs != null ? S.computingMs : 1900);
    var PHONE_MIN = Number(VALIDATE.phoneMinDigits != null ? VALIDATE.phoneMinDigits : 8);
    var LANDING = S.landingPageHandle || 'treningskalkulator';
    var PAGE_CATEGORY = S.pageCategory || attr('data-page-category') || 'quiz';

    // Step layout is derived from totalSteps so questions can be added without
    // hunting hardcoded numbers: contact is ALWAYS the last question step, then
    // computing, then the result.
    var TOTAL = Number(S.totalSteps != null ? S.totalSteps : 6);
    var STEP_CONTACT = TOTAL;
    var STEP_COMPUTING = TOTAL + 1;
    var STEP_RESULT = TOTAL + 2;

    var reducedMotion = false;
    try {
      reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {}

    // Question config indexed by key, plus id→label + "none" id maps.
    var Q = {};
    (C.questions || []).forEach(function (q) { if (q && q.key) Q[q.key] = q; });
    var LABEL = {}; // key → { optionId: label }
    var NONE = {};  // key → id of the option with none:true (if any)
    Object.keys(Q).forEach(function (k) {
      var m = {};
      (Q[k].options || []).forEach(function (o) {
        m[o.id] = o.label;
        if (o.none) NONE[k] = o.id;
      });
      LABEL[k] = m;
    });
    function labelFor(key, id) { return (LABEL[key] && LABEL[key][id]) || ''; }

    /* ---------------------------------------------------------------------- *
     * DOM handles
     * ---------------------------------------------------------------------- */
    var qScreens = root.querySelectorAll('.quiz-screen--question');
    function screenByStep(step) { return root.querySelector('.quiz-screen[data-step="' + step + '"]'); }

    var progressBlock = qs(root, '[data-quiz-progress]');
    var progressText = qs(root, '[data-quiz-progress-text]');
    var progressFill = qs(root, '[data-quiz-fill]');

    var resultEl = screenByStep(STEP_RESULT);
    var ringEl = resultEl && qs(resultEl, '[data-quiz-ring]');

    var tplReassure = qs(root, '[data-tpl="reassurance-item"]');
    var tplFocus = qs(root, '[data-tpl="focus-item"]');
    var tplReport = qs(root, '[data-tpl="report-block"]');
    var tplTimeline = qs(root, '[data-tpl="timeline-row"]');
    var tplChip = qs(root, '[data-tpl="mirror-chip"]');

    /* ---------------------------------------------------------------------- *
     * State + persistence (design state shape + our started/leadSent flags)
     * ---------------------------------------------------------------------- */
    var state = {
      step: 0,
      name: '', lastname: '', phone: '', freeText: '',
      alder: '', ringmeg: '',
      mirror: '', goals: [], body: [], doctor: '', obstacle: '', readiness: '',
      callbackRequested: false
    };
    var started = false;   // quiz_start fired this session
    var leadSent = false;  // /api/lead POST already dispatched this session
    var planStep2Default = null; // markup default for plan step 2 (owner-editable text)

    function persist() {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          step: state.step,
          name: state.name, lastname: state.lastname, phone: state.phone,
          freeText: state.freeText,
          alder: state.alder, ringmeg: state.ringmeg,
          mirror: state.mirror, goals: state.goals, body: state.body, doctor: state.doctor,
          obstacle: state.obstacle, readiness: state.readiness,
          callbackRequested: state.callbackRequested,
          _started: started, _leadSent: leadSent
        }));
      } catch (e) {}
    }
    function restore() {
      try {
        var raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        var s = JSON.parse(raw);
        if (!s || typeof s !== 'object') return;
        if (typeof s.step === 'number') state.step = s.step;
        if (typeof s.name === 'string') state.name = s.name;
        if (typeof s.lastname === 'string') state.lastname = s.lastname;
        if (typeof s.phone === 'string') state.phone = s.phone;
        if (typeof s.freeText === 'string') state.freeText = s.freeText;
        if (typeof s.alder === 'string') state.alder = s.alder;
        if (typeof s.ringmeg === 'string') state.ringmeg = s.ringmeg;
        if (typeof s.mirror === 'string') state.mirror = s.mirror;
        if (Array.isArray(s.goals)) state.goals = s.goals.slice();
        if (Array.isArray(s.body)) state.body = s.body.slice();
        if (typeof s.doctor === 'string') state.doctor = s.doctor;
        if (typeof s.obstacle === 'string') state.obstacle = s.obstacle;
        if (typeof s.readiness === 'string') state.readiness = s.readiness;
        state.callbackRequested = !!s.callbackRequested;
        started = !!s._started;
        leadSent = !!s._leadSent;
      } catch (e) {}
    }

    /* ---------------------------------------------------------------------- *
     * Token interpolation ( {name} {,name} {phone} + explicit extras )
     * ---------------------------------------------------------------------- */
    function fill(tpl, extra) {
      if (tpl == null) return '';
      var nm = state.name || '';
      var nmt = nm.trim();
      var map = {
        '{name}': nm,
        '{,name}': nmt ? ', ' + nmt : '',
        '{phone}': ((state.phone || '').trim() || CLOSE.phoneFallback || '')
      };
      if (extra) for (var k in extra) if (extra.hasOwnProperty(k)) map[k] = extra[k];
      var out = String(tpl);
      for (var t in map) if (map.hasOwnProperty(t)) out = out.split(t).join(map[t]);
      return out;
    }

    /* ---------------------------------------------------------------------- *
     * Meta / attribution context (mirrors the layout visit beacon)
     * ---------------------------------------------------------------------- */
    function readFbContext() {
      var ctx = { fbclid: null, fbp: null, fbc: null };
      try {
        var url = new URL(window.location.href);
        ctx.fbclid = url.searchParams.get('fbclid');
      } catch (e) {}
      try {
        var cookies = {};
        (document.cookie || '').split(';').forEach(function (c) {
          var idx = c.indexOf('=');
          if (idx <= 0) return;
          var k = c.slice(0, idx).trim();
          var rawv = c.slice(idx + 1);
          try { cookies[k] = decodeURIComponent(rawv); } catch (e) { cookies[k] = rawv; }
        });
        ctx.fbp = cookies._fbp || null;
        ctx.fbc = cookies._fbc || null;
      } catch (e) {}
      // Synthesize fbc from a fresh click if the cookie hasn't landed yet
      // (matches layout/quiz.liquid's beacon behavior).
      if (ctx.fbclid && !ctx.fbc) {
        ctx.fbc = 'fb.1.' + Date.now() + '.' + ctx.fbclid;
      }
      return ctx;
    }

    function fireBeacon(eventName, fbName) {
      if (!eventName) return;
      try {
        var ctx = readFbContext();
        var payload = {
          event: eventName,
          fbclid: ctx.fbclid,
          fbp: ctx.fbp,
          fbc: ctx.fbc,
          eventSourceUrl: window.location.href,
          landingPageHandle: LANDING,
          pageCategory: PAGE_CATEGORY,
          referrer: document.referrer || null
        };
        if (apiBase) {
          fetch(apiBase + (LEAD.beaconPath || '/api/visit-attribution'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
            credentials: 'omit'
          }).catch(function () {});
        }
      } catch (e) {}
      try { if (fbName && window.fbq) window.fbq('trackCustom', fbName); } catch (e) {}
    }

    function maybeFireStart() {
      if (started) return;
      started = true;
      persist();
      fireBeacon(BEACON.startEvent, BEACON.fbStart);
    }

    /* ---------------------------------------------------------------------- *
     * Quiz session analytics — /api/quiz-event beacons (ads.nono.no Quiz tab).
     *
     * Every visitor gets a random session id (per tab session) and every user
     * navigation fires a tiny beacon: step number + question id ONLY — never
     * the answers. The answers travel exclusively in the /api/lead POST the
     * visitor consents to on the contact step. Events:
     *   land       — first pageview of the tab session (sid just created)
     *   step       — every user navigation (incl. back); max(step) = how far
     *   cta_click  — a [data-booking-trigger] tap (offer card or sticky CTA)
     *   lead_sent  — /api/lead accepted (mirrors the quiz_complete beacon)
     *   restart    — «Ta testen på nytt»
     * ---------------------------------------------------------------------- */
    var quizSid = null;
    var quizLanded = false; // sid created THIS pageview → fire one 'land'
    try {
      var SID_KEY = 'nono_quiz_sid';
      try { quizSid = sessionStorage.getItem(SID_KEY) || null; } catch (e) {}
      if (!quizSid) {
        quizSid = (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : 'q-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
        quizLanded = true;
        // Storage may be blocked (Safari «Block all cookies») — keep the
        // in-memory sid so beacons, callback and lead-linkage still work;
        // cost is only a per-pageview sid in those browsers.
        try { sessionStorage.setItem(SID_KEY, quizSid); } catch (e) {}
      }
    } catch (e) {}

    function qidForStep(step) {
      if (step === 0) return 'intro';
      if (step === STEP_COMPUTING) return 'computing';
      if (step === STEP_RESULT) return 'result';
      var scr = screenByStep(step);
      return (scr && scr.getAttribute('data-qid')) || ('step-' + step);
    }

    /* ---------------------------------------------------------------------- *
     * Anonymous answer tallies — /api/quiz-answer (the «Hvem faller fra?» view).
     *
     * DELIBERATELY carries NO session id, NO Meta identifiers, NO url: just
     * {qid, answers[], completed} → day-level counters. This is how the
     * dashboard sees which ANSWERS the quitters gave as a population without
     * ever holding an individual abandoner's answers. Counted once per qid per
     * tab session (ansSent, persisted) when the visitor ADVANCES past the
     * question; the 'completed' cohort re-fires the final answers after
     * /api/lead succeeds.
     * ---------------------------------------------------------------------- */
    var ANSWER_QIDS = ['alder', 'mirror', 'goals', 'obstacle', 'body', 'doctor', 'readiness'];
    var ansSent = {};
    try { ansSent = JSON.parse(sessionStorage.getItem('nono_quiz_ans') || '{}') || {}; } catch (e) {}

    function answerValues(qid) {
      var v = state[qid];
      if (Object.prototype.toString.call(v) === '[object Array]') return v.slice(0, 12);
      return (typeof v === 'string' && v) ? [v] : [];
    }

    function trackAnswers(qid, values, completed) {
      if (!apiBase || !values.length) return;
      try {
        fetch(apiBase + '/api/quiz-answer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ qid: qid, answers: values, completed: !!completed }),
          keepalive: true,
          credentials: 'omit'
        }).catch(function () {});
      } catch (e) {}
    }

    function maybeTrackAnswer(fromStep) {
      var scr = screenByStep(fromStep);
      if (!scr) return;
      // The screen's own qid + any embedded [data-qgroup] qids (alder rides
      // on the name screen) — each counted once per tab session.
      var qids = [scr.getAttribute('data-qid')];
      var grps = scr.querySelectorAll('[data-qgroup]');
      for (var gi = 0; gi < grps.length; gi++) qids.push(grps[gi].getAttribute('data-qgroup'));
      var dirty = false;
      for (var k = 0; k < qids.length; k++) {
        var qid = qids[k];
        if (!qid || ANSWER_QIDS.indexOf(qid) === -1 || ansSent[qid]) continue;
        var values = answerValues(qid);
        if (!values.length) continue;
        ansSent[qid] = true;
        dirty = true;
        trackAnswers(qid, values, false);
      }
      if (dirty) {
        try { sessionStorage.setItem('nono_quiz_ans', JSON.stringify(ansSent)); } catch (e) {}
      }
    }

    function trackQuiz(eventName, step, retriesLeft) {
      if (!apiBase || !quizSid) return;
      try {
        var ctx = readFbContext();
        var hasStep = (typeof step === 'number');
        var bodyStr = JSON.stringify({
          sessionId: quizSid,
          event: eventName,
          step: hasStep ? step : null,
          qid: hasStep ? qidForStep(step) : null,
          landingPageHandle: LANDING,
          eventSourceUrl: window.location.href,
          referrer: document.referrer || null,
          fbclid: ctx.fbclid,
          fbp: ctx.fbp,
          fbc: ctx.fbc
        });
        var doSend = function (left) {
          fetch(apiBase + '/api/quiz-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: bodyStr,
            keepalive: true,
            credentials: 'omit'
          }).then(function (res) {
            // Retry only when asked (callback_request carries the call
            // promise) — plain step beacons stay fire-and-forget.
            if (!(res && res.ok) && left > 0) setTimeout(function () { doSend(left - 1); }, 2000);
          }).catch(function () {
            if (left > 0) setTimeout(function () { doSend(left - 1); }, 2000);
          });
        };
        doSend(retriesLeft || 0);
      } catch (e) {}
    }

    /* ---------------------------------------------------------------------- *
     * Lead capture — ONE background POST from the contact gate (step 4 → 5).
     * Never blocks the flow: we advance optimistically and POST in the
     * background (keepalive). Retry once on failure, then continue silently.
     * On 200 → quiz_complete + fbq QuizComplete.
     * ---------------------------------------------------------------------- */
    function buildDiscovery() {
      var name = (state.name || '').trim();
      var lastname = (state.lastname || '').trim();
      var mirrorLabel = labelFor('mirror', state.mirror);
      var goalLabels = (state.goals || [])
        .map(function (id) { return labelFor('goals', id); })
        .filter(Boolean)
        .join(LEAD.goalLabelJoin || ', ');
      // Contact is the LAST step, so body + doctor are already answered when
      // the lead fires — the CRM note carries the complete picture.
      var bodyLabels = (state.body || [])
        .map(function (id) { return labelFor('body', id); })
        .filter(Boolean)
        .join(LEAD.goalLabelJoin || ', ');
      var doctorLabel = labelFor('doctor', state.doctor);
      var out = String(LEAD.discoveryTemplate || '')
        .split('{name}').join(name)
        .split('{lastname}').join(lastname)
        .split('{alderLabel}').join(labelFor('alder', state.alder))
        .split('{mirrorLabel}').join(mirrorLabel)
        .split('{goalLabels}').join(goalLabels)
        .split('{bodyLabels}').join(bodyLabels)
        .split('{doctorLabel}').join(doctorLabel)
        .split('{obstacleLabel}').join(labelFor('obstacle', state.obstacle))
        .split('{readinessLabel}').join(labelFor('readiness', state.readiness))
        .split('{ringmegLabel}').join(labelFor('ringmeg', state.ringmeg));
      // Optional free text — appended only when the visitor wrote something.
      var ft = (state.freeText || '').trim();
      if (ft) out += '\n' + (LEAD.freeTextLabel || 'Notat') + ': ' + ft;
      var max = Number(LEAD.maxDiscovery || 1000);
      if (out.length > max) out = out.slice(0, max);
      return out;
    }

    function buildLeadPayload() {
      var ck = LEAD.customKeys || {};
      var custom = {};
      if (ck.ringmeg) custom[ck.ringmeg] = labelFor('ringmeg', state.ringmeg);
      if (ck.alder) custom[ck.alder] = labelFor('alder', state.alder);
      if (ck.situasjon) custom[ck.situasjon] = labelFor('mirror', state.mirror);
      if (ck.mal) custom[ck.mal] = (state.goals || []).map(function (id) { return labelFor('goals', id); }).filter(Boolean).join(', ');
      if (ck.navn) custom[ck.navn] = (state.name || '').trim();
      if (ck.kropp) custom[ck.kropp] = (state.body || []).map(function (id) { return labelFor('body', id); }).filter(Boolean).join(', ');
      if (ck.lege) custom[ck.lege] = labelFor('doctor', state.doctor);
      if (ck.hinder) custom[ck.hinder] = labelFor('obstacle', state.obstacle);
      if (ck.nar) custom[ck.nar] = labelFor('readiness', state.readiness);
      if (ck.notat && (state.freeText || '').trim()) custom[ck.notat] = state.freeText.trim();

      var payload = {
        // Clamped to the backend zod caps (100/100/32) — an over-length entry
        // must truncate, never 400 the whole lead into silent loss.
        firstName: (state.name || '').trim().slice(0, 100),
        lastName: (state.lastname || '').trim().slice(0, 100),
        phoneNumber: (state.phone || '').trim().slice(0, 32),
        consent: true,
        // Links this lead to its quiz_events session so ads.nono.no can show
        // WHO a converted session became (post-consent only, by design).
        quizSessionId: quizSid || undefined,
        // Momence's «How did you find out about us?» gets ONLY the source tag —
        // the per-field custom keys (quiz_alder…) carry the answers there now
        // (owner-confirmed 2026-07-24). The FULL readable dump still travels as
        // quizSummary: unknown to the backend's zod (never forwarded to
        // Momence) but stored in our raw payload — ads.nono.no reads it.
        discoveryAnswer: (LEAD.discoveryTag || 'Treningsprofilen (quiz)'),
        quizSummary: buildDiscovery(),
        custom: custom,
        pageUrl: window.location.href,
        // Honeypot: ALWAYS empty. The backend drops any lead whose `hp` is
        // non-empty, and 50+ browsers/password managers autofill hidden decoy
        // fields — forwarding that value would silently lose real leads.
        hp: ''
      };
      var sid = sourceId;
      if (sid != null && sid !== '' && Number(sid) !== 0 && !isNaN(Number(sid))) {
        payload.sourceId = Number(sid);
      }
      return payload;
    }

    function sendLead(payload, triesLeft) {
      if (!apiBase) return;
      fetch(apiBase + (LEAD.endpointPath || '/api/lead'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'omit'
      }).then(function (res) {
        if (res && res.ok) {
          fireBeacon(BEACON.completeEvent, BEACON.fbComplete);
          trackQuiz('lead_sent', state.step); // session analytics: converted
          // They answered «Ja, gjerne» to being called: flag the session (📞
          // in ads.nono.no) + trigger the vertskap notification chain.
          if (state.ringmeg === 'ja') trackQuiz('callback_request', state.step, 1);
          // Anonymous tallies, 'completed' cohort: the FINAL answers of a
          // session that became a lead (dashboard: quitters = all − completed).
          for (var qi = 0; qi < ANSWER_QIDS.length; qi++) {
            trackAnswers(ANSWER_QIDS[qi], answerValues(ANSWER_QIDS[qi]), true);
          }
          return;
        }
        // Non-2xx (429 / 4xx / 5xx): DELAYED retry — an immediate one shares
        // the same failure window (deploy restart, radio gap, bucket refill).
        if (triesLeft > 0) setTimeout(function () { sendLead(payload, triesLeft - 1); }, 10000);
        else leadFailedFinal();
      }).catch(function () {
        // Network / CORS / abort: same delayed-retry policy.
        if (triesLeft > 0) setTimeout(function () { sendLead(payload, triesLeft - 1); }, 10000);
        else leadFailedFinal();
      });
    }

    // All retries exhausted: UN-LATCH so the session can still recover — the
    // answers live in sessionStorage and the visitor may still be on the page.
    // Bounded re-attempts (max 2 cycles, 30s apart) so a dead network never
    // loops forever. A rare duplicate Momence lead beats a silently lost one.
    var leadRecoveries = 0;
    function leadFailedFinal() {
      leadSent = false;
      persist();
      if (leadRecoveries < 2) {
        leadRecoveries++;
        setTimeout(function () { maybeFireLead(); }, 30000);
      }
    }

    function maybeFireLead() {
      if (leadSent) return;
      if (!canContact()) return; // defensive; the Neste gate already guarantees this
      leadSent = true;           // set first so a double-advance can't double-post
      persist();
      try { sendLead(buildLeadPayload(), 2); } catch (e) {}
    }

    /* ---------------------------------------------------------------------- *
     * Booking prefill handoff — nono-own-booking.js reads this key so the
     * booking form opens with the details the visitor already gave the quiz
     * (they should never type the same information twice). Same-tab only.
     * ---------------------------------------------------------------------- */
    function writePrefill() {
      try {
        var pf = {};
        if ((state.name || '').trim()) pf.firstName = state.name.trim();
        if ((state.lastname || '').trim()) pf.lastName = state.lastname.trim();
        if ((state.phone || '').replace(/\D/g, '').length >= 6) pf.phone = state.phone.trim();
        if (pf.firstName || pf.lastName || pf.phone) {
          sessionStorage.setItem('nono_prefill_customer', JSON.stringify(pf));
        }
      } catch (e) {}
    }

    /* ---------------------------------------------------------------------- *
     * can-gating (advance eligibility) per step
     * ---------------------------------------------------------------------- */
    function canName() { return (state.name || '').trim().length > 0; }
    function canContact() {
      return (state.lastname || '').trim().length > 0 &&
        (state.phone || '').replace(/\D/g, '').length >= PHONE_MIN;
    }

    /* ---------------------------------------------------------------------- *
     * Acknowledgment + «Visste du?» fact banner (steps 3-6, per config.ack.afterStep).
     * Returns { ack, fact } — the ack is the warm human line, the fact is a TRUE,
     * verified nugget keyed by the answer the user just gave (config.facts).
     * ---------------------------------------------------------------------- */
    function ackFor(step) {
      var FACTS = C.facts || {};
      var mapKey = (ACK.afterStep || {})[String(step)];
      if (mapKey === 'mirror') {
        return {
          ack: fill((ACK.mirror || {})[state.mirror] || ''),
          fact: (FACTS.mirror || {})[state.mirror] || ''
        };
      }
      if (mapKey === 'goals') {
        var g0 = (state.goals || [])[0];
        return {
          ack: fill(ACK.goalsThanks || ''),
          fact: g0 ? ((FACTS.goals || {})[g0] || '') : ''
        };
      }
      if (mapKey === 'obstacle') {
        // The obstacle self-admission answered: «that is exactly what we are
        // built for» + a fact that speaks to THEIR named obstacle.
        return {
          ack: fill((ACK.obstacle || {})[state.obstacle] || ''),
          fact: (FACTS.obstacle || {})[state.obstacle] || ''
        };
      }
      if (mapKey === 'doctor') {
        // Shown on the readiness screen — a single warm line, no fact.
        return {
          ack: fill((ACK.doctor || {})[state.doctor] || ''),
          fact: ''
        };
      }
      if (mapKey === 'readiness') {
        // Shown on the contact screen — affirm their own commitment level
        // («utforsker» must feel just as welcome). No fact: minimal before the reveal.
        return {
          ack: fill((ACK.readiness || {})[state.readiness] || ''),
          fact: ''
        };
      }
      if (mapKey === 'body') {
        var b = state.body || [];
        var noneId = NONE.body || 'ingen';
        var ackText = '';
        if (b.length && b.indexOf(noneId) === -1) ackText = fill(ACK.bodyReal || '');
        else if (b.indexOf(noneId) !== -1) ackText = fill(ACK.bodyNone || '');
        var fact = '';
        var fmap = FACTS.body || {};
        for (var i = 0; i < b.length; i++) {
          if (fmap[b[i]]) { fact = fmap[b[i]]; break; } // first selected condition with a fact
        }
        return { ack: ackText, fact: fact };
      }
      return { ack: '', fact: '' };
    }

    /* ---------------------------------------------------------------------- *
     * Result engine — a verbatim port of computeProfile()
     * ---------------------------------------------------------------------- */
    function hasAny(arr, ids) {
      if (!arr || !ids) return false;
      for (var i = 0; i < ids.length; i++) if (arr.indexOf(ids[i]) !== -1) return true;
      return false;
    }
    function inList(list, val) { return !!list && list.indexOf(val) !== -1; }

    function computeProfile() {
      var body = state.body || [], goals = state.goals || [];
      var pPost = PROFILE.postOp || {}, pPain = PROFILE.pain || {};
      var postOp = inList(pPost.mirror, state.mirror) || hasAny(body, pPost.bodyAny);
      var pain = inList(pPain.mirror, state.mirror) || hasAny(body, pPain.bodyAny);

      var A = PROFILE.archetypes || {};
      var chosen;
      if (postOp) chosen = A.postOp;
      else if (pain) chosen = A.pain;
      else if (state.mirror === 'aldri') chosen = A.aldri || A.utrent;
      else if (state.mirror === 'utrent') chosen = A.utrent;
      else chosen = A.default;
      chosen = chosen || {};

      // match — start base, cap.
      var M = PROFILE.match || {};
      var noneId = NONE.body || 'ingen';
      var bodyReal = body.length > 0 && body.indexOf(noneId) === -1;
      var match = Number(M.base != null ? M.base : 88);
      var dMap = M.doctor || {};
      if (dMap[state.doctor] != null) match += Number(dMap[state.doctor]);
      if (bodyReal) match += Number(M.bodyReal || 0);
      if (pain || postOp) match += Number(M.painOrPostOp || 0);
      if (goals.length >= 2) match += Number(M.goalsMin2 || 0);
      var cap = Number(M.cap != null ? M.cap : 97);
      match = Math.min(cap, match);

      // reassurance — build in config order, dedupe by text, keep max.
      var rules = PROFILE.reassurance || [];
      var picked = [], seen = {};
      var reassureMax = Number(PROFILE.reassureMax || 4);
      for (var r = 0; r < rules.length; r++) {
        var rule = rules[r];
        var ok = false;
        if (rule.if === 'always') ok = true;
        else if (rule.if === 'postOp') ok = postOp;
        else if (rule.if === 'painOrBodyAny') ok = pain || hasAny(body, rule.bodyAny);
        else if (rule.if === 'bodyAny') ok = hasAny(body, rule.bodyAny);
        if (!ok) continue;
        var txt = rule.text;
        if (!txt || seen[txt] || picked.length >= reassureMax) continue;
        seen[txt] = 1;
        picked.push({ text: txt });
      }

      // focus — from goals (or a default set), mapped through FMAP, capped.
      var FMAP = PROFILE.focus || {};
      var focusIds = goals.length ? goals : (PROFILE.focusDefault || []);
      var focus = [];
      var focusMax = Number(PROFILE.focusMax || 4);
      for (var f = 0; f < focusIds.length && focus.length < focusMax; f++) {
        var item = FMAP[focusIds[f]];
        if (item) focus.push(item);
      }

      // goalPhrase — from the FIRST selected goal, else default.
      var PMAP = PROFILE.goalPhrase || {};
      var goalPhrase = goals.length
        ? (PMAP[goals[0]] || PROFILE.goalPhraseDefault || '')
        : (PROFILE.goalPhraseDefault || '');

      // bodyPhrase — the FIRST selected body condition that has a phrase (serious
      // conditions deliberately have no phrase, so they never appear beside the score).
      var BPMAP = PROFILE.bodyPhrase || {};
      var bodyPhrase = '';
      for (var bp = 0; bp < body.length; bp++) {
        if (BPMAP[body[bp]]) { bodyPhrase = BPMAP[body[bp]]; break; }
      }

      var rt = PROFILE.reassureTitle || {};
      var reassureTitle = (postOp || pain) ? rt.painOrPostOp : rt.default;

      return {
        archetype: chosen.name || '',
        blurb: chosen.blurb || '',
        match: match,
        reassurance: picked,
        focus: focus,
        goalPhrase: goalPhrase,
        bodyPhrase: bodyPhrase,
        reassureTitle: reassureTitle || '',
        isPostOp: postOp,
        isPain: pain
      };
    }

    function cloneTpl(tpl) {
      if (!tpl) return null;
      if (tpl.content && tpl.content.firstElementChild) {
        return tpl.content.firstElementChild.cloneNode(true);
      }
      // Fallback for engines without <template>.content.
      var wrap = document.createElement('div');
      wrap.innerHTML = (tpl.innerHTML || '').trim();
      return wrap.firstElementChild;
    }

    function hasPhone() {
      return (state.phone || '').replace(/\D/g, '').length >= PHONE_MIN;
    }

    function updateCallbackUI() {
      if (!resultEl) return;
      var ask = qs(resultEl, '[data-quiz-callback-ask]');
      var done = qs(resultEl, '[data-quiz-callback-done]');
      var callus = qs(resultEl, '[data-quiz-callus]');
      var btn = qs(resultEl, '[data-slot="callback-btn"]');
      if (btn) setText(btn, fill(CLOSE.fallbackBtnTemplate));
      // GDPR skip: no number captured → offer OUR number instead of promising a call.
      if (!hasPhone()) {
        if (ask) ask.hidden = true;
        if (done) done.hidden = true;
        if (callus) callus.hidden = false;
        return;
      }
      if (callus) callus.hidden = true;
      if (state.callbackRequested) {
        if (ask) ask.hidden = true;
        if (done) {
          done.hidden = false;
          setText(qs(done, '[data-slot="callback-done"]'), fill(CLOSE.fallbackDoneTemplate));
        }
      } else {
        if (ask) ask.hidden = false;
        if (done) done.hidden = true;
      }
    }

    function fillResult() {
      if (!resultEl) return;
      var p = computeProfile();

      setText(qs(resultEl, '[data-slot="greeting"]'), fill(RESULT.greetingTemplate));
      setText(qs(resultEl, '[data-slot="archetype"]'), p.archetype);
      // Analysis explanation — nevro/hjerte get the NEUTRAL variant (same rail
      // as the offer lede/step2): no rest-vs-strength framing, doctor first.
      var heroSensitive = (state.body || []).indexOf('nevro') !== -1 ||
        (state.body || []).indexOf('hjerte') !== -1;
      setText(qs(resultEl, '[data-slot="blurb"]'),
        (heroSensitive && PROFILE.analysisNeutral) ? PROFILE.analysisNeutral : p.blurb);
      // v5: the match-% is still computed (p.match) but deliberately NOT rendered —
      // the reveal is the archetype + their own answers, not a score.

      // Mirror chips — their OWN answers as pills: first real condition (or the
      // situation label), first goal, and the doctor's recommendation when given.
      var chipsEl = qs(resultEl, '[data-slot="mirror-chips"]');
      if (chipsEl) {
        chipsEl.textContent = '';
        var chips = [];
        var noneIdC = NONE.body || 'ingen';
        var firstCond = null;
        for (var ci = 0; ci < (state.body || []).length; ci++) {
          if (state.body[ci] !== noneIdC) { firstCond = state.body[ci]; break; }
        }
        if (firstCond) chips.push(labelFor('body', firstCond));
        else if (state.mirror) chips.push(labelFor('mirror', state.mirror));
        if ((state.goals || [])[0]) chips.push(labelFor('goals', state.goals[0]));
        if (state.doctor === 'ja' && RESULT.doctorChip) chips.push(RESULT.doctorChip);
        for (var cj = 0; cj < chips.length && cj < 3; cj++) {
          if (!chips[cj]) continue;
          var chip = cloneTpl(tplChip);
          if (!chip) continue;
          setText(chip, chips[cj]);
          chipsEl.appendChild(chip);
        }
      }

      // Free-text echo — quoted back to the visitor themselves, never paraphrased.
      var echoEl = qs(resultEl, '[data-slot="freetext-echo"]');
      if (echoEl) {
        var ftxt = (state.freeText || '').trim();
        if (ftxt) {
          if (ftxt.length > 90) ftxt = ftxt.slice(0, 90).replace(/\s+\S*$/, '') + ' …';
          setText(echoEl, 'Du skrev: «' + ftxt + '»');
          echoEl.hidden = false;
        } else {
          echoEl.hidden = true;
        }
      }

      var ul = qs(resultEl, '[data-slot="reassurance"]');
      if (ul) {
        ul.textContent = '';
        for (var i = 0; i < p.reassurance.length; i++) {
          var li = cloneTpl(tplReassure);
          if (!li) continue;
          setText(qs(li, '.quiz-reassure-list__text'), p.reassurance[i].text);
          ul.appendChild(li);
        }
      }

      // v5: the goal-tiles and timeline cards are GONE (owner decision 2026-07-15).
      // config.focus/timelines remain as dead data; p.focus and the timeline
      // variants are deliberately not rendered.

      // Personal report — «Om kroppen din»: doctor-keyed intro + the first
      // REPORT.max condition blocks visible; the rest behind a native accordion.
      var REPORT = C.report || {};
      var repCard = qs(resultEl, '[data-quiz-report]');
      if (repCard) {
        var hasReport = false;
        if (REPORT.title) setText(qs(repCard, '[data-slot="report-title"]'), REPORT.title);
        var introEl = qs(repCard, '[data-slot="report-intro"]');
        var introTxt = (REPORT.introDoctor || {})[state.doctor] || '';
        if (introEl) {
          if (introTxt) { setText(introEl, fill(introTxt)); introEl.hidden = false; hasReport = true; }
          else introEl.hidden = true;
        }
        var blocksEl = qs(repCard, '[data-slot="report-blocks"]');
        var moreWrap = qs(repCard, '[data-quiz-more]');
        var moreBlocksEl = qs(repCard, '[data-slot="report-blocks-more"]');
        if (blocksEl) {
          blocksEl.textContent = '';
          if (moreBlocksEl) moreBlocksEl.textContent = '';
          var bmap = REPORT.body || {};
          var noneId2 = NONE.body || 'ingen';
          var sel = (state.body || []).filter(function (id) { return id !== noneId2 && bmap[id]; });
          var blocks = sel.length ? sel.map(function (id) { return bmap[id]; })
                                  : (REPORT.none ? [REPORT.none] : []);
          var maxB = Number(REPORT.max || 2);
          function renderBlock(target, b) {
            var bl = cloneTpl(tplReport);
            if (!bl) return;
            setText(qs(bl, '.quiz-report__block-title'), b.title || '');
            setText(qs(bl, '.quiz-report__block-line'), b.line || b.science || '');
            target.appendChild(bl);
          }
          for (var bi = 0; bi < blocks.length && bi < maxB; bi++) renderBlock(blocksEl, blocks[bi]);
          var rest = blocks.slice(maxB);
          if (moreWrap) {
            if (rest.length && moreBlocksEl) {
              for (var bj = 0; bj < rest.length; bj++) renderBlock(moreBlocksEl, rest[bj]);
              setText(qs(moreWrap, '[data-slot="report-more-label"]'),
                (REPORT.moreLabel || 'Det andre du krysset av') + ' (' + rest.length + ')');
              moreWrap.hidden = false;
              moreWrap.open = false;
            } else {
              moreWrap.hidden = true;
            }
          }
          if (blocks.length) hasReport = true;
        }
        repCard.hidden = !hasReport;
      }

      // The offer (2 free weeks) — benefit-neutral variant for the nevro/hjerte
      // segments: never promise felt changes to an ME or cardiac reader. The
      // lede carries the «kjenn det selv»-promise, so it is hidden for them too.
      var step2El = qs(resultEl, '[data-slot="plan-step2"]');
      var ledeEl = qs(resultEl, '[data-slot="offer-lede"]');
      var bodySel = state.body || [];
      var sensitive = bodySel.indexOf('nevro') !== -1 || bodySel.indexOf('hjerte') !== -1;
      if (step2El) {
        if (planStep2Default == null) planStep2Default = step2El.textContent;
        var PLAN = C.plan || {};
        setText(step2El, (sensitive && PLAN.step2Neutral) ? PLAN.step2Neutral : planStep2Default);
      }
      if (ledeEl) ledeEl.hidden = sensitive;

      // Obstacle echo — the close answers the obstacle THEY named (hidden on the
      // sensitive nevro/hjerte path, like the lede).
      var obstEl = qs(resultEl, '[data-slot="obstacle-line"]');
      if (obstEl) {
        var obstTxt = ((C.offer || {}).obstacleLines || {})[state.obstacle] || '';
        if (obstTxt && !sensitive) { setText(obstEl, fill(obstTxt)); obstEl.hidden = false; }
        else obstEl.hidden = true;
      }

      // Prefill continuity note — only when we actually HAVE their number.
      var pfNote = qs(resultEl, '[data-quiz-prefillnote]');
      if (pfNote) pfNote.hidden = !hasPhone();

      setText(qs(resultEl, '[data-slot="goalphrase"]'), p.goalPhrase);
      setText(qs(resultEl, '[data-slot="close-name"]'), state.name || '');

      updateCallbackUI();
    }

    /* ---------------------------------------------------------------------- *
     * Option marks (all question screens — cheap, keeps restore correct)
     * ---------------------------------------------------------------------- */
    function markOptions() {
      for (var s = 0; s < qScreens.length; s++) {
        var scr = qScreens[s];
        var qid = scr.getAttribute('data-qid');
        var kind = scr.getAttribute('data-kind');
        if (kind !== 'single' && kind !== 'multi') continue;
        var val = state[qid];
        var opts = scr.querySelectorAll('[data-quiz-option]');
        for (var o = 0; o < opts.length; o++) {
          var v = opts[o].getAttribute('data-value');
          var sel = kind === 'multi'
            ? (Array.isArray(val) && val.indexOf(v) !== -1)
            : (val === v);
          opts[o].classList.toggle('is-selected', sel);
          opts[o].setAttribute('aria-checked', sel ? 'true' : 'false');
        }
      }
      // Embedded groups ([data-qgroup], e.g. alder on the name screen) — the
      // screen loop above skips text-kind screens, so paint these separately.
      var groups = root.querySelectorAll('[data-qgroup]');
      for (var g = 0; g < groups.length; g++) {
        var gval = state[groups[g].getAttribute('data-qgroup')];
        var gopts = groups[g].querySelectorAll('[data-quiz-option]');
        for (var go = 0; go < gopts.length; go++) {
          var gsel = gval === gopts[go].getAttribute('data-value');
          gopts[go].classList.toggle('is-selected', gsel);
          gopts[go].setAttribute('aria-checked', gsel ? 'true' : 'false');
        }
      }
    }

    /* ---------------------------------------------------------------------- *
     * paint — sync dynamic content for the CURRENT step (no scroll/focus)
     * ---------------------------------------------------------------------- */
    function updateProgress(step) {
      var isQ = (step >= 1 && step <= TOTAL);
      if (progressBlock) progressBlock.hidden = !isQ;
      if (isQ) {
        if (progressText) {
          setText(progressText, fill(PROGRESS.textTemplate || 'Steg {n} av ' + TOTAL, { '{n}': String(step) }));
        }
        if (progressFill) progressFill.style.width = Math.round((step / TOTAL) * 100) + '%';
      }
    }

    function paint() {
      var step = state.step;
      markOptions();

      var scr = screenByStep(step);
      if (!scr) return;

      if (step >= 1 && step <= TOTAL) {
        // «Visste du?» fact banner (fed by the previous answer). The ♥-ack
        // receipts («Takk, {name} …») were KILLED per owner 2026-07-22 — they
        // read as filler; the fact carries the value alone now.
        var factEl = qs(scr, '[data-quiz-fact]');
        var ai = ackFor(step);
        if (factEl) {
          if (ai.fact) { setText(qs(factEl, '[data-slot="fact"]'), ai.fact); factEl.hidden = false; }
          else factEl.hidden = true;
        }
        // Per-situasjon sub: a question may carry subByMirror overrides so
        // narrow lines (e.g. the 9-av-10 belonging proof) only show behind
        // narrow answers. Falls back to the question's static sub.
        var subEl = qs(scr, '[data-quiz-sub]');
        if (subEl) {
          var qcfgSub = Q[scr.getAttribute('data-qid')] || {};
          var subTxt = (qcfgSub.subByMirror || {})[state.mirror] || qcfgSub.sub || '';
          if (subTxt) setText(subEl, subTxt);
        }
        // dynamic contact title (contact is always the LAST question step)
        if (step === STEP_CONTACT) {
          var q = Q.contact || {};
          setText(qs(scr, '[data-slot="contact-title"]'), fill(q.titleTemplate || ''));
        }
        // Neste button (label + enabled) for text/multi/contact steps. Gating is
        // derived from the screen's data-kind so steps can be reordered freely.
        var next = qs(scr, '[data-quiz-next]');
        if (next) {
          var kind = scr.getAttribute('data-kind');
          var qid = scr.getAttribute('data-qid');
          var can = false, label = NEXTLABELS.default || 'Neste →';
          if (kind === 'text') {
            var nmt = (state.name || '').trim();
            can = nmt.length > 0;
            // Embedded groups on this screen (alder) must also be answered.
            var reqGroups = scr.querySelectorAll('[data-qgroup]');
            for (var rg = 0; rg < reqGroups.length; rg++) {
              if (!state[reqGroups[rg].getAttribute('data-qgroup')]) can = false;
            }
            label = nmt
              ? fill(NEXTLABELS.nameTemplate || label, { '{name}': nmt })
              : (NEXTLABELS.default || label);
          } else if (kind === 'multi') {
            can = (state[qid] || []).length > 0;
          } else if (kind === 'single') {
            can = !!state[qid];
          } else if (kind === 'contact') {
            can = canContact();
            var cGroups = scr.querySelectorAll('[data-qgroup]');
            for (var cg = 0; cg < cGroups.length; cg++) {
              if (!state[cGroups[cg].getAttribute('data-qgroup')]) can = false;
            }
            label = NEXTLABELS.contact || label;
          }
          next.disabled = !can;
          setText(next, label);
        }
      } else if (step === STEP_COMPUTING) {
        setText(qs(scr, '[data-slot="computing-title"]'), fill((C.computing || {}).titleTemplate || ''));
      } else if (step === STEP_RESULT) {
        fillResult();
      }
    }

    /* ---------------------------------------------------------------------- *
     * Screen machine
     * ---------------------------------------------------------------------- */
    var booted = false;
    var pickTimer = null;
    var compTimer = null;

    function showOnly(step) {
      var screens = root.querySelectorAll('.quiz-screen');
      for (var i = 0; i < screens.length; i++) {
        var el = screens[i];
        var isActive = String(el.getAttribute('data-step')) === String(step);
        if (isActive) { el.hidden = false; el.classList.add('is-active'); }
        else { el.classList.remove('is-active'); el.hidden = true; }
      }
    }

    function transition(step) {
      state.step = step;
      persist();
      // Reaching computing/result = the quiz is done — hand the contact details
      // to the booking widget so its form opens prefilled.
      if (step >= STEP_COMPUTING) writePrefill();
      showOnly(step);
      updateProgress(step);
      paint();
      if (stickyUpdate) stickyUpdate(); // sticky CTA is result-step-only
    }

    function focusHeading() {
      var scr = screenByStep(state.step);
      if (scr) focusEl(qs(scr, '[tabindex="-1"]'));
    }

    // A user-initiated navigation: switch screen, scroll to top, move focus.
    function navigate(step, moveFocus) {
      transition(step);
      trackQuiz('step', step); // analytics: which screen they ARRIVED at
      if (booted) {
        scrollTop();
        if (moveFocus) focusHeading();
      }
    }

    /* ---------------------------------------------------------------------- *
     * Actions — verbatim ports of start / goNext / back / pick / toggle / …
     * ---------------------------------------------------------------------- */
    function start() {
      maybeFireStart(); // OUR: quiz_start beacon + fbq QuizStart
      navigate(1, true);
    }

    function startComputing() {
      if (compTimer) clearTimeout(compTimer);
      compTimer = setTimeout(function () {
        compTimer = null;
        if (state.step === STEP_COMPUTING) navigate(STEP_RESULT, true);
      }, COMPUTING_MS);
    }

    function goNext() {
      var from = state.step;
      var next = Math.min(from + 1, STEP_RESULT);
      maybeTrackAnswer(from); // anonymous tally for the question being LEFT
      navigate(next, true);

      // OUR: securing the warm lead the instant they clear the contact gate
      // (contact is the LAST question step — every answer is in the payload).
      if (from === STEP_CONTACT) maybeFireLead();

      if (next === STEP_COMPUTING) startComputing();
    }

    // GDPR un-gate: advance from the contact step to the result WITHOUT
    // capturing a phone or firing /api/lead. Viewing the profile is never gated
    // behind the phone number (freely-given consent); only the optional callback
    // fallback needs it. Deliberately does NOT call maybeFireLead().
    function skipContact() {
      if (state.step !== STEP_CONTACT) return;
      navigate(STEP_COMPUTING, true);
      startComputing();
    }

    function back() {
      if (pickTimer) { clearTimeout(pickTimer); pickTimer = null; }
      // Never rest on the transient computing screen (no timer armed there
      // when entered backwards — it would strand an infinite spinner).
      var prev = state.step - 1;
      if (prev === STEP_COMPUTING) prev = STEP_CONTACT;
      navigate(Math.max(prev, 0), true);
    }

    function pick(key, val) {
      state[key] = val;
      persist();
      paint();
      // NO auto-advance (owner 2026-07-22): single-choice steps used to jump
      // ~240ms after a tap, so a mis-tap sent people forward on the wrong
      // answer. Now every step waits for an explicit «Neste».
    }

    function toggle(key, val, isNone) {
      var arr = (state[key] || []).slice();
      var noneId = NONE[key] || 'ingen';
      if (isNone) {
        arr = arr.indexOf(val) !== -1 ? [] : [val];
      } else {
        arr = arr.filter(function (x) { return x !== noneId; });
        if (arr.indexOf(val) !== -1) arr = arr.filter(function (x) { return x !== val; });
        else arr.push(val);
      }
      state[key] = arr;
      persist();
      paint();
    }

    function onInput(inputEl) {
      var f = inputEl.getAttribute('data-quiz-input'); // name | lastname | phone | freetext
      if (f === 'freetext') {
        state.freeText = inputEl.value;
        persist();
        return; // no paint needed — never gates the Neste button
      }
      if (f !== 'name' && f !== 'lastname' && f !== 'phone') return;
      state[f] = inputEl.value;
      persist();
      paint(); // updates Neste label/enabled + the dynamic contact title
    }

    function requestCallback() {
      state.callbackRequested = true;
      persist();
      updateCallbackUI();
      // The request must reach a HUMAN: this beacon triggers a Slack message
      // to vertskapet (name + number via the linked lead) and flags the
      // session in ads.nono.no. (The old fireBeacon signal went to the
      // attribution endpoint, which silently dropped it — the button promised
      // a call nobody heard about.)
      trackQuiz('callback_request', state.step);
      if (resultEl) {
        var done = qs(resultEl, '[data-slot="callback-done"]');
        if (done) focusEl(done);
      }
    }

    function restart() {
      if (pickTimer) { clearTimeout(pickTimer); pickTimer = null; }
      if (compTimer) { clearTimeout(compTimer); compTimer = null; }
      state.name = ''; state.lastname = ''; state.phone = ''; state.freeText = '';
      state.alder = ''; state.ringmeg = '';
      state.mirror = ''; state.goals = []; state.body = []; state.doctor = '';
      state.obstacle = ''; state.readiness = '';
      state.callbackRequested = false;
      // started persists (one quiz_start beacon per session), but leadSent
      // RESETS on an explicit restart (owner 2026-07-23): «Ta kartleggingen på
      // nytt» + a new submit is a deliberate new run — possibly a new person
      // on a shared device — and must create a fresh Momence lead + link it to
      // the session. The guard still stops accidental double-submits WITHIN a
      // run. Answer tallies reset with it so the completed cohort matches.
      leadSent = false;
      ansSent = {};
      try { sessionStorage.setItem('nono_quiz_ans', JSON.stringify(ansSent)); } catch (e) {}
      persist();
      trackQuiz('restart', null);
      setInputValue('name', '');
      setInputValue('lastname', '');
      setInputValue('phone', '');
      setInputValue('freetext', '');
      navigate(0, true);
    }

    function onOption(opt) {
      var scr = closest(opt, '.quiz-screen--question');
      if (!scr) return;
      // An embedded [data-qgroup] (e.g. the alder radios on the name screen)
      // carries its own qid/kind — the screen's text-kind must not swallow it.
      var grp = closest(opt, '[data-qgroup]');
      var qid = grp ? grp.getAttribute('data-qgroup') : scr.getAttribute('data-qid');
      var kind = grp ? (grp.getAttribute('data-qkind') || 'single') : scr.getAttribute('data-kind');
      var val = opt.getAttribute('data-value');
      if (kind === 'single') pick(qid, val);
      else if (kind === 'multi') toggle(qid, val, opt.getAttribute('data-none') === 'true');
    }

    /* ---------------------------------------------------------------------- *
     * Inputs
     * ---------------------------------------------------------------------- */
    function inputByRole(rname) { return root.querySelector('[data-quiz-input="' + rname + '"]'); }
    function setInputValue(rname, v) {
      var el = inputByRole(rname);
      if (el && el.value !== v) el.value = v;
    }

    /* ---------------------------------------------------------------------- *
     * visualViewport — keep the contact "Neste" pill above the mobile keyboard
     * ---------------------------------------------------------------------- */
    function wireKeyboardInset() {
      if (!window.visualViewport) return;
      var vv = window.visualViewport;
      var prevKb = 0;
      function onVV() {
        try {
          var contact = screenByStep(STEP_CONTACT);
          if (!contact) return;
          var kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
          var ae = document.activeElement;
          var inContact = ae && contact.contains(ae);
          contact.style.paddingBottom = (kb && inContact) ? (kb + 24) + 'px' : '';
          // Re-center the pill ONLY when the keyboard just opened (0 → positive),
          // so an iOS 'scroll' near a focused field doesn't fight the user.
          if (prevKb === 0 && kb > 0 && inContact) {
            requestAnimationFrame(function () {
              // Reveal the FIELD being typed in ({block:'nearest'} = minimal
              // motion). Centering the pill pushed the focused input off the
              // top edge now that ringmeg/consent/freetext sit between them.
              try { if (ae && ae.scrollIntoView) ae.scrollIntoView({ block: 'nearest' }); }
              catch (e) {}
            });
          }
          prevKb = kb;
        } catch (e) {}
      }
      vv.addEventListener('resize', onVV);
      vv.addEventListener('scroll', onVV);
    }

    /* ---------------------------------------------------------------------- *
     * Sticky CTA (result step only) — reveals once the reveal hero has
     * scrolled above the viewport; hides while the offer card is on screen or
     * the booking modal is open (mirrors the LP's initSticky pattern).
     * ---------------------------------------------------------------------- */
    var stickyUpdate = null;
    function wireSticky() {
      var wrap = qs(root, '[data-quiz-sticky]');
      var hero = qs(root, '[data-quiz-hero]');
      var offer = qs(root, '[data-quiz-offer]');
      if (!wrap || !hero || !('IntersectionObserver' in window)) return;

      var pastHero = false, offerInView = false, modalOpen = false;
      function update() {
        var show = state.step === STEP_RESULT && pastHero && !offerInView && !modalOpen;
        wrap.classList.toggle('is-visible', show);
      }
      wrap.hidden = false; // visibility is driven by .is-visible from here on

      new IntersectionObserver(function (entries) {
        var e = entries[0];
        pastHero = !e.isIntersecting && e.boundingClientRect.top < 0;
        update();
      }).observe(hero);

      if (offer) {
        new IntersectionObserver(function (entries) {
          offerInView = entries[0].isIntersecting;
          update();
        }, { threshold: 0.15 }).observe(offer);
      }

      // The booking modal is server-rendered by the lp-own-booking section and
      // toggles [hidden]; this script is deferred, so it exists by now.
      var modal = document.querySelector('.nob-modal');
      if (modal && 'MutationObserver' in window) {
        modalOpen = !modal.hidden;
        new MutationObserver(function () { modalOpen = !modal.hidden; update(); })
          .observe(modal, { attributes: true, attributeFilter: ['hidden'] });
      }

      stickyUpdate = update;
    }

    /* ---------------------------------------------------------------------- *
     * Event wiring
     * ---------------------------------------------------------------------- */
    function wire() {
      // Delegated clicks across the whole app.
      root.addEventListener('click', function (e) {
        var t = e.target;
        if (closest(t, '[data-quiz-start]')) { start(); return; }
        if (closest(t, '[data-quiz-back]')) { back(); return; }
        var next = closest(t, '[data-quiz-next]');
        if (next) { if (!next.disabled) goNext(); return; }
        if (closest(t, '[data-quiz-skip]')) { skipContact(); return; }
        var opt = closest(t, '[data-quiz-option]');
        if (opt) { onOption(opt); return; }
        if (closest(t, '[data-quiz-callback]')) { requestCallback(); return; }
        if (closest(t, '[data-quiz-restart]')) { restart(); return; }
        // [data-booking-trigger] is intentionally NOT handled here —
        // nono-own-booking.js catches it via document-level delegation.
      });

      // Analytics only: log booking-CTA taps (offer card + sticky). Document-
      // level and non-exclusive — never interferes with nono-own-booking.js.
      document.addEventListener('click', function (e) {
        if (closest(e.target, '[data-booking-trigger]')) trackQuiz('cta_click', state.step);
      });

      // Inputs (name / lastname / phone).
      var inputs = root.querySelectorAll('[data-quiz-input]');
      for (var i = 0; i < inputs.length; i++) {
        (function (el) {
          el.addEventListener('input', function () { onInput(el); });
          el.addEventListener('keydown', function (e) {
            if (e.key !== 'Enter' && e.keyCode !== 13) return;
            var role = el.getAttribute('data-quiz-input');
            // Enter advances ONLY when the screen's Neste is enabled — the
            // button gating is the single source of truth (it also requires
            // the embedded groups: alder on the name screen, ringmeg on the
            // contact screen). canName()/canContact() alone would bypass them.
            if (role === 'name' || role === 'phone') {
              e.preventDefault();
              var actScr = screenByStep(state.step);
              var actNext = actScr && qs(actScr, '[data-quiz-next]');
              if (actNext && !actNext.disabled) goNext();
            } else if (role === 'lastname') {
              e.preventDefault();
              focusEl(inputByRole('phone'));
            }
          });
        })(inputs[i]);
      }

      /* Tapping the question TEXT focuses that screen's field.
         Clarity (25 July) recorded six dead clicks on «Først — hva heter du?»
         across two of the six visitors who started — a third of them tapped the
         heading expecting to type and nothing happened. The heading is an <h2>
         wired to the input via aria-labelledby, which names the field for screen
         readers but gives no click target; a real <label for> would steal the
         focus target the step-change animation relies on. So: keep the markup,
         add the click surface. Only fires on screens that actually have a field,
         and never hijacks a tap on a link/button inside the text. */
      root.addEventListener('click', function (e) {
        var hit = closest(e.target, '.quiz-card__title, .quiz-card__sub');
        if (!hit) return;
        if (closest(e.target, 'a, button, input, textarea, label, [data-quiz-option]')) return;
        var scr = closest(hit, '.quiz-screen');
        if (!scr) return;
        // VISIBLE fields only. The contact screen's free-text note lives inside
        // a collapsed <details>; focusing a hidden element is a silent no-op, so
        // the tap would stay just as dead as the one this fix exists to cure.
        var all = scr.querySelectorAll('[data-quiz-input]');
        var visible = [];
        for (var fi = 0; fi < all.length; fi++) {
          if (all[fi].offsetParent !== null) visible.push(all[fi]);
        }
        if (!visible.length) return;
        // First EMPTY visible field (contact has two) so the tap lands where the
        // visitor still has something to type; else the first one.
        var field = visible[0];
        for (var vi = 0; vi < visible.length; vi++) {
          if (!String(visible[vi].value || '').trim()) { field = visible[vi]; break; }
        }
        focusEl(field);
      });

      // Keyboard: Esc = Back (only when not typing in a field).
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' && e.keyCode !== 27) return;
        // Yield to an open booking modal — Esc there closes the modal and
        // must not ALSO navigate the quiz backwards.
        if (document.querySelector('.nob-modal:not([hidden])')) return;
        var t = e.target;
        var typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' || t.isContentEditable);
        if (!typing) back();
      });

      wireKeyboardInset();
      wireSticky();
    }

    /* ---------------------------------------------------------------------- *
     * Boot
     * ---------------------------------------------------------------------- */
    function boot() {
      restore();

      // Rehydrate the free-text fields from restored state.
      setInputValue('name', state.name);
      setInputValue('lastname', state.lastname);
      setInputValue('phone', state.phone);
      setInputValue('freetext', state.freeText);

      var step = state.step;
      if (typeof step !== 'number' || step < 0 || step > STEP_RESULT) step = 0;
      // Never rest on the transient computing screen: on reload jump to the
      // result (all answers are already captured by the time step 7 was shown).
      if (step === STEP_COMPUTING) step = STEP_RESULT;

      transition(step); // booted=false → no scroll, no focus (honours "no autofocus")
      booted = true;
      // Analytics: one 'land' per tab session (sid freshly created). Reloads
      // mid-quiz reuse the sid and fire nothing here.
      if (quizLanded) trackQuiz('land', step);
    }

    wire();
    boot();
  } catch (e) {
    // Never throw to the user; the <noscript> phone fallback covers hard failure.
  }
})();
